import { useState, useEffect, useCallback } from "react";
import { API_CONFIG } from "../config/apiConfig";
import { Line } from "../types";
import { apiClient } from "../utils/apiClient";

interface ApiResponse {
  success?: boolean;
  data?: any[];
  error?: string;
}

// Define type for line item mapping
interface LineItem {
  description: string;
  serialNumber: string;
  assetNumber: string;
  validatedSerialNumber?: string;
  validatedAssetNumber?: string;
  itemNumber: string;
  toSubInv?: string;
  toLocator?: string;
  validatedSerialItem?: string;
  status?: string;
  trackingNumber?: string;
  internalSalesOrder?: string;
  isoLineNumber?: string;
  repairEligibility?: string;
  replaceIrNumber?: string;
  replaceIrLineNum?: string;
  returnHeaderId: string;
  returnLineId: string;
  radReceiptDate?: string;
}

export const useLineDetails = (returnHeaderId: string | undefined) => {
  const [lines, setLines] = useState<Line[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Function to fetch validated serial number item
  const fetchValidatedSnItem = useCallback(async (serialNumber: string): Promise<{itemNumber: string; description: string; validatedAssetNumber: string}> => {
    try {
      const { data } = await apiClient.get(
        API_CONFIG.ENDPOINTS.GET_VALIDATED_SN_ITEM,
        {
          params: {
            serial_number: String(serialNumber || '').trim(),
          },
        }
      );
      
      // Handle nested response structure
      let responseData = data;
      if (data.data && data.data.statusCode) {
        if (data.data.statusCode === 500) {
          const errorBody = JSON.parse(data.data.body);
          console.error('Validated SN Item Database Error:', errorBody.error);
          return { itemNumber: '', description: '', validatedAssetNumber: '' };
        }
        responseData = JSON.parse(data.data.body);
      }
      
      if (responseData.success && responseData.data && responseData.data.length > 0) {
        return {
          itemNumber: responseData.data[0].segment1 || '',
          description: responseData.data[0].description || '',
          validatedAssetNumber: responseData.data[0].validatedAssetNumber || ''
        };
      }
      return { itemNumber: '', description: '', validatedAssetNumber: '' };
    } catch (error) {
      console.error('Validated SN Item Error:', error);
      return { itemNumber: '', description: '', validatedAssetNumber: '' };
    }
  }, []);

  // Function to fetch replacement order details
  const fetchReplacementOrderDetails = useCallback(async (rmaOrder: string, replaceIrNumber: string, replaceIrLineNum: string) => {
    try {
      const { data } = await apiClient.get(
        API_CONFIG.ENDPOINTS.GET_REPLACEMENT_ORDER_DTLS,
        {
          params: {
            p_rma_order: rmaOrder,
            p_replace_ir_number: replaceIrNumber,
            p_replace_ir_line_num: replaceIrLineNum,
          },
        }
      );
      
      if (data.success && data.data && data.data.length > 0) {
        const orderData = data.data[0];
        return {
          internalSalesOrder: orderData.orderNumber || '',
          isoLineNumber: orderData.lineNumber || '',
          replacementOrderLineStatus: orderData.lineStatus || '',
        };
      }
      return { internalSalesOrder: '', isoLineNumber: '', replacementOrderLineStatus: '' };
    } catch (error) {
      console.error('Replacement Order Details Error:', error);
      return { internalSalesOrder: '', isoLineNumber: '', replacementOrderLineStatus: '' };
    }
  }, []);

  // Memoize the mapping function
  const mapLineData = useCallback(
    async (item: LineItem, headerId: string): Promise<Line> => {
      let validatedSerialItem = item.validatedSerialItem || "";
      let validatedDescription = item.description || "";
      let validatedAssetNumber = item.validatedAssetNumber || "";
      
      // If validatedSerialNumber exists, fetch the item, description, and validatedAssetNumber
      if (item.validatedSerialNumber && item.validatedSerialNumber.trim()) {
        const validatedData = await fetchValidatedSnItem(item.validatedSerialNumber);
        validatedSerialItem = validatedData.itemNumber;
        // Use validated description if original description is empty
        if (!validatedDescription.trim() && validatedData.description.trim()) {
          validatedDescription = validatedData.description;
        }
        // Use validated asset number if original is empty
        if (!validatedAssetNumber.trim() && validatedData.validatedAssetNumber.trim()) {
          validatedAssetNumber = validatedData.validatedAssetNumber;
        }
      }

      // Fetch replacement order details if replaceIrNumber and replaceIrLineNum exist
      let replacementOrderData = { internalSalesOrder: '', isoLineNumber: '', replacementOrderLineStatus: '' };
      if (item.replaceIrNumber && item.replaceIrLineNum) {
        replacementOrderData = await fetchReplacementOrderDetails(headerId, item.replaceIrNumber, item.replaceIrLineNum);
      }
      
      // Parse toLocator to extract pallet, cage, and tote
      const parseToLocator = (toLocator: string) => {
        if (!toLocator) return { pallet: '', cage: '', tote: '' };
        const segments = toLocator.split('.');
        return {
          pallet: segments[0] || '',
          cage: segments[1] || '',
          tote: segments[2] || ''
        };
      };
      
      const locatorData = parseToLocator(item.toLocator || '');
      
      return {
        id: `${headerId}-${item.returnLineId}-${item.serialNumber || Math.random()}`,
        description: validatedDescription,
        serialNumber: item.serialNumber,
        assetNumber: item.assetNumber,
        validatedSerialNumber: item.validatedSerialNumber || "",
        validatedAssetNumber: validatedAssetNumber,
        itemNumber: item.itemNumber,
        pallet: locatorData.pallet,
        subinventory: '',
        cage: locatorData.cage,
        tote: locatorData.tote,
        toSubinventory: item.toSubInv || "",
        toLocator: item.toLocator || "",
        originalToSubinventory: item.toSubInv || "",
        originalToLocator: item.toLocator || "",
        validatedSerialItem,
        status: item.status || "",
        trackingNumber: item.trackingNumber || "",
        internalSalesOrder: replacementOrderData.internalSalesOrder || item.internalSalesOrder || "",
        isoLineNumber: replacementOrderData.isoLineNumber || item.isoLineNumber || "",
        replacementOrderLineStatus: replacementOrderData.replacementOrderLineStatus || "",
        repairEligibility: item.repairEligibility || "",
        radReceiptDate: item.radReceiptDate || "",
        replaceIrNumber: item.replaceIrNumber || "",
        replaceIrLineNum: item.replaceIrLineNum || "",
        returnHeaderId: item.returnHeaderId,
        returnLineId: item.returnLineId,
      };
    },
    [fetchValidatedSnItem, fetchReplacementOrderDetails]
  );

  // Memoize the API call function
  const fetchLines = useCallback(
    async (retryCount = 0): Promise<void> => {
      if (!returnHeaderId) {
        setLines([]);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { data } = await apiClient.get<ApiResponse>(
          API_CONFIG.ENDPOINTS.GET_LINES,
          {
            params: {
              return_header_id: String(returnHeaderId || '').trim(),
            },
          }
        );

        const lineData = Array.isArray(data) ? data : data.data;

        if (Array.isArray(lineData)) {
          const mappedLines = await Promise.all(
            lineData.map((item) => mapLineData(item, returnHeaderId))
          );
          setLines(mappedLines);
        } else {
          console.error("Invalid response format:", data);
          setLines([]);
        }
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message;

        if (errorMessage === "Database error" && retryCount < 2) {
          setTimeout(() => fetchLines(retryCount + 1), 1000 * (retryCount + 1));
          return;
        }

        setError(errorMessage);
        setLines([]);
      } finally {
        setLoading(false);
      }
    },
    [returnHeaderId, mapLineData]
  );

  useEffect(() => {
    fetchLines();
  }, [fetchLines]);

  return { lines, loading, error };
};

// import { useState, useEffect } from 'react';
// import axios from 'axios';
// import { API_CONFIG } from '../config/apiConfig';
// import { Line } from '../types';

// export const useLineDetails = (returnHeaderId: string | undefined) => {
//   const [lines, setLines] = useState<Line[]>([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState<string | null>(null);

//   useEffect(() => {
//     const fetchLines = async (retryCount = 0) => {
//       if (!returnHeaderId) {
//         setLines([]);
//         return;
//       }

//       setLoading(true);
//       setError(null);

//       try {
//         const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_LINES}`;
//         const response = await axios.get(url, {
//           params: { return_header_id: returnHeaderId },
//           timeout: API_CONFIG.TIMEOUT,
//           headers: {
//             'Content-Type': 'application/json',
//           }
//         });

//         const data = response.data;
//         console.log('Line Details API Response:', data);

//         if (data && Array.isArray(data)) {
//           const mappedLines = data.map((item: any) => ({
//             id: `${returnHeaderId}-${item.serialNumber || item.returnLineId || Math.random()}`,
//             description: item.description,
//             serialNumber: item.serialNumber,
//             assetNumber: item.assetNumber,
//             validatedSerialNumber: item.validatedSerialNumber || '',
//             validatedAssetNumber: item.validatedAssetNumber || '',
//             itemNumber: item.itemNumber,
//             toSubinventory: item.toSubInv || '',
//             toLocator: item.toLocator || '',
//             isSerialValid: item.isSerialValid === 'Y',
//             validatedSerialItem: item.validatedSerialItem || '',
//             validatedSerialSite: item.validatedSerialSite || '',
//             status: item.status || '',
//             returnHeaderId: item.returnHeaderId,
//             returnLineId: item.returnLineId
//           }));
//           setLines(mappedLines);
//         } else if (data.success && data.data && Array.isArray(data.data)) {
//           const mappedLines = data.data.map((item: any) => ({
//             id: `${returnHeaderId}-${item.serialNumber || item.returnLineId || Math.random()}`,
//             description: item.description,
//             serialNumber: item.serialNumber,
//             assetNumber: item.assetNumber,
//             validatedSerialNumber: item.validatedSerialNumber || '',
//             validatedAssetNumber: item.validatedAssetNumber || '',
//             itemNumber: item.itemNumber,
//             toSubinventory: item.toSubInv || '',
//             toLocator: item.toLocator || '',
//             isSerialValid: item.isSerialValid === 'Y',
//             validatedSerialItem: item.validatedSerialItem || '',
//             validatedSerialSite: item.validatedSerialSite || '',
//             status: item.status || '',
//             returnHeaderId: item.returnHeaderId,
//             returnLineId: item.returnLineId
//           }));
//           setLines(mappedLines);
//         } else {
//           console.error('Invalid response format:', data);
//           setLines([]);
//         }
//       } catch (err: any) {
//         const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message;

//         // Retry on database errors (max 2 retries)
//         if (errorMessage === 'Database error' && retryCount < 2) {
//           console.log(`Database error, retrying... (${retryCount + 1}/2)`);
//           setTimeout(() => fetchLines(retryCount + 1), 1000 * (retryCount + 1));
//           return;
//         }

//         setError(errorMessage);
//         setLines([]);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchLines();
//   }, [returnHeaderId]);

//   return { lines, loading, error };
// };
